import { componentKeyToEntityTypeMapping } from "@/src/constants/dashboardMappings.js";

const requiresConfig = (componentId) =>
  componentId in componentKeyToEntityTypeMapping;

export default {
  namespaced: true,

  state: {
    dashboardDetails: null,
    showCardList: false,
    dashboardLayout: {
      rows: [],
    },
    activeSection: null,
    components: [],
    dashboardFilters: [],
    editingSections: {},
    savedSnapshot: null,
    excludeComponents: [],
  },

  mutations: {
    SET_DASHBOARD_DETAILS(state, value) {
      state.dashboardDetails = value;
    },

    SET_SHOW_CARD_LIST(state, value) {
      state.showCardList = value;
    },

    SET_EXCLUDE_COMPONENTS(state, value) {
      state.excludeComponents = value;
    },

    TOGGLE_CARD_LIST(state) {
      state.showCardList = !state.showCardList;
    },

    SET_DASHBOARD_LAYOUT(state, layout) {
      state.dashboardLayout = layout;
    },

    ADD_LAYOUT_ROW(state, { columns }) {
      const newRow = {
        position: state.dashboardLayout.rows.length,
        columns: columns,
        sections: [],
      };

      for (let i = 0; i < columns; i++) {
        newRow.sections.push({
          position: i,
          component_id: null,
          config: {},
        });
      }

      state.dashboardLayout.rows.push(newRow);
    },

    UPDATE_SECTION_COMPONENT(state, { rowIndex, sectionIndex, componentId }) {
      if (
        state.dashboardLayout.rows[rowIndex] &&
        state.dashboardLayout.rows[rowIndex].sections[sectionIndex]
      ) {
        state.dashboardLayout.rows[rowIndex].sections[
          sectionIndex
        ].component_id = componentId;

        if (componentId === null) {
          state.dashboardLayout.rows[rowIndex].sections[sectionIndex].config =
            {};
        }
      }
    },

    UPDATE_SECTION_CONFIG(state, { rowIndex, sectionIndex, config }) {
      if (
        state.dashboardLayout.rows[rowIndex] &&
        state.dashboardLayout.rows[rowIndex].sections[sectionIndex]
      ) {
        const newConfig = {
          ...state.dashboardLayout.rows[rowIndex].sections[sectionIndex].config,
          ...config,
        };
        
        Object.keys(newConfig).forEach(key => {
          if (newConfig[key] === undefined) {
            delete newConfig[key];
          }
        });
        
        state.dashboardLayout.rows[rowIndex].sections[sectionIndex].config = newConfig;
      }
    },

    SET_ACTIVE_SECTION(state, section) {
      state.activeSection = section;
    },

    SET_COMPONENTS(state, components) {
      state.components = components;
    },

    UPDATE_DASHBOARD_NAME(state, name) {
      if (state.dashboardDetails) {
        state.dashboardDetails.name = name;
      }
    },

    SET_DASHBOARD_FILTERS(state, filters) {
      state.dashboardFilters = filters;
    },

    REORDER_SECTIONS(state, { rowIndex, newSections }) {
      if (state.dashboardLayout.rows[rowIndex]) {
        state.dashboardLayout.rows[rowIndex].sections = newSections.map(
          (section, index) => ({
            ...section,
            position: index,
          }),
        );
      }
    },

    REORDER_ROWS(state, { newRows }) {
      state.dashboardLayout.rows = newRows.map((row, index) => ({
        ...row,
        position: index,
      }));
    },

    SWAP_SECTIONS(state, { sourceRow, sourceSection, targetRow, targetSection }) {
      if (
        state.dashboardLayout.rows[sourceRow] &&
        state.dashboardLayout.rows[targetRow] &&
        state.dashboardLayout.rows[sourceRow].sections[sourceSection] &&
        state.dashboardLayout.rows[targetRow].sections[targetSection]
      ) {
        const source = state.dashboardLayout.rows[sourceRow].sections[sourceSection];
        const target = state.dashboardLayout.rows[targetRow].sections[targetSection];
        
        state.dashboardLayout.rows[sourceRow].sections[sourceSection] = {
          ...target,
          position: sourceSection,
        };
        state.dashboardLayout.rows[targetRow].sections[targetSection] = {
          ...source,
          position: targetSection,
        };
      }
    },

    CONVERT_TO_SINGLE_COLUMN(state, { rowIndex, sectionToKeep }) {
      if (state.dashboardLayout.rows[rowIndex]) {
        const row = state.dashboardLayout.rows[rowIndex];
        const keptSection = row.sections[sectionToKeep];
        
        row.columns = 1;
        row.sections = [{
          ...keptSection,
          position: 0,
        }];
      }
    },

    DELETE_ROW(state, { rowIndex }) {
      if (state.dashboardLayout.rows[rowIndex]) {
        state.dashboardLayout.rows.splice(rowIndex, 1);
        
        state.dashboardLayout.rows.forEach((row, index) => {
          row.position = index;
        });
      }
    },

    SET_SECTION_EDITING(state, { rowIndex, sectionIndex }) {
      const key = `${rowIndex}-${sectionIndex}`;
      state.editingSections = { ...state.editingSections, [key]: true };
    },

    CLEAR_SECTION_EDITING(state, { rowIndex, sectionIndex }) {
      const key = `${rowIndex}-${sectionIndex}`;
      const updated = { ...state.editingSections };
      delete updated[key];
      state.editingSections = updated;
    },

    CLEAR_ALL_EDITING(state) {
      state.editingSections = {};
    },

    SAVE_SNAPSHOT(state) {
      state.savedSnapshot = JSON.stringify({
        name: state.dashboardDetails?.name || "",
        layout: state.dashboardLayout,
      });
    },

    RESET_DASHBOARD_STATE(state) {
      state.dashboardDetails = null;
      state.showCardList = false;
      state.dashboardLayout = { rows: [] };
      state.activeSection = null;
      state.dashboardFilters = [];
      state.editingSections = {};
      state.savedSnapshot = null;
      state.excludeComponents = [];
    },
  },

  actions: {
    setDashboardDetails({ commit }, value) {
      commit("SET_DASHBOARD_DETAILS", value);
      commit("CLEAR_ALL_EDITING");
      if (value?.layout) {
        commit("SET_DASHBOARD_LAYOUT", value.layout);
      } else {
        commit("SET_DASHBOARD_LAYOUT", { rows: [] });
      }
      commit("SAVE_SNAPSHOT");
    },

    setShowCardList({ commit }, value) {
      commit("SET_SHOW_CARD_LIST", value);
    },

    setExcludeComponents({ commit }, value) {
      commit("SET_EXCLUDE_COMPONENTS", value);
    },

    toggleCardListState({ commit }) {
      commit("TOGGLE_CARD_LIST");
    },

    setDashboardLayout({ commit }, layout) {
      commit("SET_DASHBOARD_LAYOUT", layout);
    },

    addLayoutRow({ commit }, { columns }) {
      commit("ADD_LAYOUT_ROW", { columns });
    },

    updateSectionComponent(
      { commit },
      { rowIndex, sectionIndex, componentId },
    ) {
      commit("UPDATE_SECTION_COMPONENT", {
        rowIndex,
        sectionIndex,
        componentId,
      });
      if (componentId !== null) {
        commit("SET_SECTION_EDITING", { rowIndex, sectionIndex });
      } else {
        commit("CLEAR_SECTION_EDITING", { rowIndex, sectionIndex });
      }
    },

    updateSectionConfig({ commit }, { rowIndex, sectionIndex, config }) {
      commit("UPDATE_SECTION_CONFIG", {
        rowIndex,
        sectionIndex,
        config,
      });
    },

    setActiveSection({ commit }, section) {
      commit("SET_ACTIVE_SECTION", section);
    },

    setComponents({ commit }, components) {
      commit("SET_COMPONENTS", components);
    },

    updateDashboardName({ commit }, name) {
      commit("UPDATE_DASHBOARD_NAME", name);
    },

    setDashboardFilters({ commit }, filters) {
      commit("SET_DASHBOARD_FILTERS", filters);
    },

    reorderSections({ commit }, { rowIndex, newSections }) {
      commit("REORDER_SECTIONS", { rowIndex, newSections });
    },

    reorderRows({ commit }, { newRows }) {
      commit("REORDER_ROWS", { newRows });
    },

    swapSections({ commit }, { sourceRow, sourceSection, targetRow, targetSection }) {
      commit("SWAP_SECTIONS", { sourceRow, sourceSection, targetRow, targetSection });
    },

    convertToSingleColumn({ commit }, { rowIndex, sectionToKeep }) {
      commit("CONVERT_TO_SINGLE_COLUMN", { rowIndex, sectionToKeep });
    },

    deleteRow({ commit }, { rowIndex }) {
      commit("DELETE_ROW", { rowIndex });
    },

    setSectionEditing({ commit }, { rowIndex, sectionIndex }) {
      commit("SET_SECTION_EDITING", { rowIndex, sectionIndex });
    },

    clearSectionEditing({ commit }, { rowIndex, sectionIndex }) {
      commit("CLEAR_SECTION_EDITING", { rowIndex, sectionIndex });
    },

    clearAllEditing({ commit }) {
      commit("CLEAR_ALL_EDITING");
    },

    saveSnapshot({ commit }) {
      commit("SAVE_SNAPSHOT");
    },

    resetDashboardState({ commit }) {
      commit("RESET_DASHBOARD_STATE");
    },
  },

  getters: {
    dashboardDetails: (state) => state.dashboardDetails,
    dashboardID: (state) => state.dashboardDetails?.id,
    showCardList: (state) => state.showCardList,
    excludeComponents: (state) => state.excludeComponents,
    dashboardLayout: (state) => state.dashboardLayout,
    activeSection: (state) => state.activeSection,
    components: (state) => state.components,
    dashboardFilters: (state) => state.dashboardFilters,
    editingSections: (state) => state.editingSections,
    isSectionEditing: (state) => (rowIndex, sectionIndex) => {
      return !!state.editingSections[`${rowIndex}-${sectionIndex}`];
    },
    hasLayouts: (state) => state.dashboardLayout?.rows?.length > 0,
    hasDashboardName: (state) => !!state.dashboardDetails?.name?.trim(),
    allSectionsConfigured: (state) => {
      const rows = state.dashboardLayout?.rows;
      if (!rows || rows.length === 0) return false;
      return rows.every((row) =>
        row.sections?.every((section) => section.component_id !== null),
      );
    },
    allSectionsHaveConfig: (state) => {
      const rows = state.dashboardLayout?.rows;
      if (!rows || rows.length === 0) return false;
      return rows.every((row) =>
        row.sections?.every((section) => {
          if (section.component_id === null) return true;
          const component = state.components.find(
            (c) => c.id === section.component_id,
          );
          if (!component || !requiresConfig(component.component_key)) {
            return true;
          }
          return section.config && Object.keys(section.config).length > 0;
        }),
      );
    },
    hasAnySectionEditing: (state) => {
      return Object.keys(state.editingSections).length > 0;
    },
    hasUnsavedChanges: (state) => {
      if (!state.savedSnapshot) return false;
      const current = JSON.stringify({
        name: state.dashboardDetails?.name || "",
        layout: state.dashboardLayout,
      });
      return current !== state.savedSnapshot;
    },
  },
};
